import React, { useEffect, useState } from "react";
import VideoTool from "../../components/tools/VideoTool";
import MusicTool from "../../components/tools/MusicTool";
import { getLyricSync, isSyncLive } from "../../services/lyricSyncService";
// Imported explicitly rather than relying on DynamicPortalPage having done
// it: CSS imports are global, but that only holds while this component is
// never rendered from anywhere else.
import "./layoutBuilder.css";

// LYRIC SPACING
// -------------
// Every entry in topic.lyrics used to render as a <p> carrying the browser
// default 1em top and bottom margin. Two consequences, both of which made a
// verse break authored in the Topic Library look like it had not saved:
//
//   1. Adjacent paragraph margins COLLAPSE, so the gap between two lines of
//      the same verse was already a full line high. Every line looked like a
//      verse of its own and nothing stood out as a break.
//   2. An empty entry rendered as an empty <p>, whose margins collapse against
//      both neighbours in exactly the same way. It contributed no height at
//      all, so a blank row was invisible even once it was stored.
//
// So lines are set tight and a blank entry becomes a spacer element with a
// real, explicit height. Now the blank row in the editor is the only thing
// that produces a gap, and it produces an obvious one.
//
// The rules live here rather than in layoutBuilder.css because that file
// carries the whole popup and portal-page palette twice over; adding a third
// place to look for lyric styling would not help anyone. They are scoped under
// .portal-popup-box so they cannot reach the preview container's own .lyrics
// block or anything else on the page.
//
// SING-ALONG
// ----------
// Opening the Music popup reads ONE document, lyricSync/{slug}. If it is
// published and was timed against this exact song file, the popup shows the
// sing-along player (lyrics a page at a time, current line lit) instead of the
// audio bar and the static lyric list. Anything else — no document, not
// published yet, the song file has since been replaced, a read error — falls
// back to the original popup exactly as it was. The sing-along can never
// stop a song from playing.

const LYRIC_STYLES = `
  .portal-popup-box .lyrics-container .lyrics-line {
    margin: 0 0 2px;
    line-height: 1.5;
  }
  .portal-popup-box .lyrics-container .lyrics-line:last-child {
    margin-bottom: 0;
  }
  .portal-popup-box .lyrics-container .lyrics-break {
    height: 20px;
  }
`;

const PortalMediaPopup = ({
  type,
  topic,
  layout,
  onClose,
  onProgressUpdate,
}) => {
  // undefined = still checking, null = none / not usable, object = live sync
  const [lyricSync, setLyricSync] = useState(type === "music" ? undefined : null);

  useEffect(() => {
    if (type !== "music" || !topic?.slug) {
      setLyricSync(null);
      return undefined;
    }
    let alive = true;
    setLyricSync(undefined);
    (async () => {
      try {
        const sync = await getLyricSync(topic.slug);
        if (alive) setLyricSync(isSyncLive(sync, topic.musicUrl) ? sync : null);
      } catch (err) {
        if (alive) setLyricSync(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [type, topic?.slug, topic?.musicUrl]);

  if (!topic || !layout) return null;

  const subjectKey = `${layout.state}_${layout.grade}_${layout.subject}`;

  // Tolerates lyrics stored as a single newline-joined string as well as the
  // array the builder writes, so an older hand-authored topics file does not
  // render as one unbroken run-on line.
  const lyricLines = Array.isArray(topic.lyrics)
    ? topic.lyrics.map((line) => String(line ?? ""))
    : typeof topic.lyrics === "string"
    ? topic.lyrics.split("\n")
    : [];

  return (
    <div className="portal-popup-overlay">
      <div className="portal-popup-box">
        <style>{LYRIC_STYLES}</style>

        <button
          type="button"
          onClick={onClose}
          className="portal-popup-close"
          aria-label="Close"
          title="Close"
        >
          ✕
        </button>

        {type === "video" && (
          <VideoTool
            videoUrl={topic.videoUrl}
            subjectKey={subjectKey}
            slug={topic.slug}
            onProgressUpdate={async () => {
              if (onProgressUpdate) {
                await onProgressUpdate();
              }
              onClose();
            }}
          />
        )}

        {type === "singalong" && (
          // The sing-along MP4 plays exactly like the Video button, through
          // the same player, and earns its own "Sing-along Star".
          <VideoTool
            videoUrl={topic.singAlongUrl}
            subjectKey={subjectKey}
            slug={topic.slug}
            progressKey="singalong"
            starLabel="Sing-along"
            onProgressUpdate={async () => {
              if (onProgressUpdate) {
                await onProgressUpdate();
              }
              onClose();
            }}
          />
        )}

        {type === "music" && lyricSync === undefined && (
          // One small read; holding the player back for it means the audio
          // element is never swapped out from under a child who already
          // pressed play.
          <p style={{ textAlign: "center", margin: "28px 0", color: "#5b6b8c" }}>
            Loading song…
          </p>
        )}

        {type === "music" && lyricSync !== undefined && (
          <div>
            <MusicTool
              musicUrl={topic.musicUrl}
              subjectKey={subjectKey}
              slug={topic.slug}
              lyricSync={lyricSync}
              title={topic.title}
              onProgressUpdate={async () => {
                if (onProgressUpdate) {
                  await onProgressUpdate();
                }
                onClose();
              }}
            />

            {!lyricSync && lyricLines.length > 0 && (
              <div className="lyrics-container">
                {lyricLines.map((line, index) =>
                  line.trim() === "" ? (
                    // aria-hidden: a screen reader announcing "blank" between
                    // every verse is noise, and the visual gap already carries
                    // the only meaning this element has.
                    <div
                      key={index}
                      className="lyrics-break"
                      aria-hidden="true"
                    />
                  ) : (
                    <p key={index} className="lyrics-line">
                      {line}
                    </p>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalMediaPopup;
